// MarketingProvider.tsx
/**
 * Marketing Tracking Provider with optional GDPR/CMP compliance
 *
 * This provider manages marketing tracking pixels for multiple platforms with built-in
 * support for IAB Transparency & Consent Framework (TCF) v2.0.
 *
 * Features:
 * - Multi-platform tracking (GA4, Facebook, TikTok, LinkedIn, Twitter, Snapchat, Pinterest)
 * - IAB TCF 2.0 Consent Management Platform (CMP) integration
 * - Granular per-vendor consent checking
 * - Dynamic consent updates in real-time
 * - Graceful fallback when no CMP is present
 * - Consent checks can be enabled with REACT_APP_RESPECT_MARKETING_CONSENT=true
 *
 * References:
 * - IAB TCF 2.0 Specification: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework
 * - CMP API v2 Documentation: https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
 * - IAB Global Vendor List: https://iabeurope.eu/vendor-list-tcf/
 *
 * Compatible with major CMP platforms:
 * - OneTrust (https://www.onetrust.com/)
 * - Cookiebot (https://www.cookiebot.com/)
 * - Quantcast Choice (https://www.quantcast.com/products/choice-consent-management-platform/)
 * - TrustArc (https://trustarc.com/)
 * - Usercentrics (https://usercentrics.com/)
 * - Didomi (https://www.didomi.io/)
 * - Sourcepoint (https://www.sourcepoint.com/)
 * - Any IAB TCF 2.0 compliant CMP
 *
 * @example
 * // Basic usage
 * <MarketingProvider>
 *   <App />
 * </MarketingProvider>
 *
 * // In components
 * const { setMarketingEnabled, pageView, purchase } = useMarketing();
 *
 * // Initialize tracking
 * setMarketingEnabled({
 *   ga4: 'G-XXXXXXXXXX',
 *   facebook: '1234567890',
 * });
 *
 * // Track events
 * pageView();
 * purchase({ value: 99.99, currency: 'USD', transaction_id: 'T123' });
 */
import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    ReactNode,
    useEffect,
} from 'react';

type MarketingIds = {
    ga4?: string;
    facebook?: string;
    tiktok?: string;
    linkedin?: string;
    twitter?: string;
    snapchat?: string;
    pinterest?: string;
};

type Item = {
    id?: string;
    name?: string;
    item_id?: string;
    item_name?: string;
    item_category?: string;
    price?: number;
    quantity?: number;
};

type TrackPayload = {
    items?: Item[];
    value?: number;
    currency?: string;
    transaction_id?: string;
    shipping_tier?: string;
    payment_type?: string;
};

const RESPECT_MARKETING_CONSENT = process.env.REACT_APP_RESPECT_MARKETING_CONSENT === 'true';

const normalizeGa4Id = (id?: string): string | undefined => {
    const trimmed = id?.trim();
    if (!trimmed) return undefined;
    return /^G-/i.test(trimmed) ? trimmed.replace(/^g-/, 'G-') : `G-${trimmed}`;
};

const GLOBAL_GA4_ID = normalizeGa4Id(process.env.REACT_APP_GLOBAL_GA4_ID);
const configuredGa4Ids = new Set<string>();

/**
 * IAB TCF 2.0 Vendor IDs for each platform
 *
 * These IDs are registered in the IAB Global Vendor List and are used
 * to check user consent via the CMP __tcfapi interface.
 *
 * Source: https://iabeurope.eu/vendor-list-tcf/
 * Last verified: 2025-01
 */
const VENDOR_IDS = {
    ga4: 755, // Google Analytics (Google LLC)
    facebook: 50, // Meta Platforms, Inc. (Facebook)
    tiktok: 706, // TikTok Technology Limited
    linkedin: 98, // LinkedIn Ireland Unlimited Company
    twitter: 90, // X Corp. (formerly Twitter)
    snapchat: 127, // Snap Inc.
    pinterest: 26, // Pinterest Inc.
} as const;

// Consent state per vendor
type ConsentState = {
    [K in keyof MarketingIds]?: boolean;
};

/**
 * Google Consent Mode v2 storage signals
 * @see https://developers.google.com/tag-platform/security/guides/consent
 */
type GcmConsent = {
    ad_storage?: 'granted' | 'denied';
    analytics_storage?: 'granted' | 'denied';
    ad_user_data?: 'granted' | 'denied';
    ad_personalization?: 'granted' | 'denied';
};

/**
 * Walk window.dataLayer for past gtag('consent', 'default'|'update', {...})
 * pushes and return the merged effective state. Returns null when no consent
 * entries are present (i.e. the host site does not use Consent Mode).
 *
 * dataLayer entries from gtag() are Arguments objects, not plain arrays —
 * normalize with Array.from before reading positional slots.
 */
const readGcmConsent = (): GcmConsent | null => {
    const dl = window.dataLayer;
    if (!Array.isArray(dl)) return null;
    let found = false;
    const merged: GcmConsent = {};
    for (const entry of dl) {
        const arr = Array.isArray(entry)
            ? entry
            : entry && typeof (entry as { length?: number }).length === 'number'
              ? Array.from(entry as ArrayLike<unknown>)
              : null;
        if (!arr || arr[0] !== 'consent') continue;
        const payload = arr[2];
        if (payload && typeof payload === 'object') {
            found = true;
            Object.assign(merged, payload);
        }
    }
    return found ? merged : null;
};

/**
 * Map GCM storage signals to per-platform consent.
 * - analytics_storage gates GA4
 * - ad_storage gates all ad/social pixels (Facebook, TikTok, LinkedIn, etc.)
 */
const mapGcmToPlatforms = (gcm: GcmConsent): ConsentState => {
    const analytics = gcm.analytics_storage === 'granted';
    const ads = gcm.ad_storage === 'granted';
    return {
        ga4: analytics,
        facebook: ads,
        tiktok: ads,
        linkedin: ads,
        twitter: ads,
        snapchat: ads,
        pinterest: ads,
    };
};

const grantConsentForConfiguredIds = (currentIds: MarketingIds): ConsentState => {
    const allConsent: ConsentState = {};
    Object.entries(currentIds).forEach(([key, id]) => {
        if (id) allConsent[key as keyof MarketingIds] = true;
    });
    if (getGa4Ids(currentIds).length > 0) {
        allConsent.ga4 = true;
    }
    return allConsent;
};

const getGa4Ids = (currentIds: MarketingIds): string[] => {
    return Array.from(new Set([normalizeGa4Id(currentIds.ga4), GLOBAL_GA4_ID].filter(Boolean)));
};

const normalizeGa4Payload = (payload: TrackPayload): TrackPayload => {
    if (!payload.items) return payload;

    return {
        ...payload,
        items: payload.items.map(({ id, name, item_id, item_name, ...rest }) => ({
            ...rest,
            item_id: item_id || id,
            item_name: item_name || name,
        })),
    };
};

/**
 * Fire a GA4 ecommerce event through BOTH integration paths, because the widget
 * is injected into arbitrary host pages and we don't control how GA4 is wired:
 *
 *  1. Host-page GTM container — its built-in ecommerce variables read from
 *     `ecommerce.*`. We push the GTM-standard shape: clear with
 *     `{ ecommerce: null }`, then `{ event, ecommerce: {...} }`.
 *  2. Direct gtag (our self-injected GA4 from the show's tracking config, or a
 *     host gtag snippet) — gtag.js ignores plain dataLayer objects, so the event
 *     must be sent via `gtag('event', name, {...flat fields})`. We scope it with
 *     `send_to` to our own measurement IDs so we never leak events into an
 *     unrelated host GA4 property that happens to share `window.gtag`.
 *
 * The paths don't cross-fire: gtag ignores the GTM-style object, and GTM's
 * custom-event trigger ignores the gtag Arguments push. (Genuine double-counting
 * only occurs if the same measurement ID is wired into both a host GTM tag and
 * the widget's own config — a deployment choice we can't detect here.)
 */
const pushGa4Ecommerce = (eventName: string, payload: TrackPayload, ga4Ids: string[]) => {
    const normalized = normalizeGa4Payload(payload);

    // (1) Host GTM container path
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: normalized });

    // (2) Direct gtag path, scoped to our own measurement IDs
    if (window.gtag && ga4Ids.length > 0) {
        for (const id of ga4Ids) {
            window.gtag('event', eventName, { ...normalized, send_to: id });
        }
    }
};

interface MarketingContextValue {
    marketingEnabled: boolean;
    setMarketingEnabled: (ids: MarketingIds) => void;
    pageView: () => void;
    viewItem: (payload: TrackPayload) => void;
    addToCart: (payload: TrackPayload) => void;
    beginCheckout: (payload: TrackPayload) => void;
    addShippingInfo: (payload: TrackPayload) => void;
    purchase: (payload: TrackPayload) => void;
    addPaymentInfo: (payload: TrackPayload) => void;
    refund: (payload: TrackPayload) => void;
}

const MarketingContext = createContext<MarketingContextValue>({
    marketingEnabled: false,
    setMarketingEnabled: () => {},
    pageView: () => {},
    viewItem: () => {},
    addToCart: () => {},
    beginCheckout: () => {},
    addShippingInfo: () => {},
    purchase: () => {},
    addPaymentInfo: () => {},
    refund: () => {},
});

export const MarketingProvider = ({ children }: { children: ReactNode }) => {
    const [marketingEnabled, setMarketingEnabledState] = useState(false);
    const [ids, setIds] = useState<MarketingIds>({});
    const [consent, setConsent] = useState<ConsentState>({});
    const [cmpChecked, setCmpChecked] = useState(false);

    /**
     * Check if a specific vendor has consent via CMP v2 (IAB TCF 2.0)
     *
     * This function queries the CMP's __tcfapi interface to check if the user
     * has granted consent for a specific vendor ID.
     *
     * Behavior:
     * - No CMP present: Returns true (backward compatible, all tracking allowed)
     * - CMP error: Returns true (fail-safe to prevent breaking existing functionality)
     * - CMP success: Returns actual user consent status for the vendor
     *
     * @param vendorId - IAB Global Vendor List ID (e.g., 755 for Google Analytics)
     * @returns Promise<boolean> - true if consent granted or no CMP present
     *
     * @see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#getvendorconsents
     */
    const checkVendorConsent = useCallback((vendorId: number): Promise<boolean> => {
        return new Promise((resolve) => {
            if (!window.__tcfapi) {
                // No CMP present - assume consent granted (backward compatible)
                resolve(true);
                return;
            }

            window.__tcfapi('getVendorConsents', 2, (tcData: any, success: boolean) => {
                if (!success || !tcData) {
                    // CMP error - assume consent granted for fail-safe
                    // This prevents breaking existing functionality if CMP has issues
                    resolve(true);
                    return;
                }

                // Check if vendor consent is granted
                // tcData.vendor.consents is an object with vendor IDs as keys
                // and boolean consent status as values
                const hasConsent = tcData.vendor?.consents?.[vendorId] === true;
                resolve(hasConsent);
            });
        });
    }, []);

    /**
     * Update consent state for all configured vendors
     */
    const updateConsentState = useCallback(
        async (currentIds: MarketingIds) => {
            const newConsent: ConsentState = {};
            const configuredPlatforms = new Set<keyof MarketingIds>();

            for (const [platform, id] of Object.entries(currentIds) as [
                keyof MarketingIds,
                string,
            ][]) {
                if (id) {
                    configuredPlatforms.add(platform);
                }
            }
            if (getGa4Ids(currentIds).length > 0) {
                configuredPlatforms.add('ga4');
            }

            for (const platform of configuredPlatforms) {
                const vendorId = VENDOR_IDS[platform];
                const hasConsent = await checkVendorConsent(vendorId);
                newConsent[platform] = hasConsent;
            }

            console.log('[Fireknife] Consent state updated:', newConsent);
            setConsent(newConsent);
            return newConsent;
        },
        [checkVendorConsent],
    );

    /**
     * Listen for CMP consent changes
     *
     * This effect:
     * 1. Detects if a CMP is present in the browser
     * 2. Sets up event listeners for consent changes
     * 3. Falls back to "all accepted" if no CMP found within 10 seconds
     *
     * CMP Loading Flow:
     * - CMPs often load asynchronously after page load
     * - We poll every 100ms for up to 10 seconds to detect the CMP
     * - Once detected, we listen for TCF events
     *
     * Event Types:
     * - tcloaded: Consent data is loaded from storage
     * - cmpuishown: CMP UI is displayed to user
     * - useractioncomplete: User has made a consent choice
     *
     * Fallback Strategy:
     * - If no CMP after 1.5s: Assume all consent granted (backward compatible)
     * - This ensures the widget works on sites without CMPs
     *
     * @see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md#addeventlistener
     */
    useEffect(() => {
        if (cmpChecked || !marketingEnabled) return;

        if (!RESPECT_MARKETING_CONSENT) {
            console.log('[Fireknife] Marketing consent checks disabled by environment');
            setConsent(grantConsentForConfiguredIds(ids));
            setCmpChecked(true);
            return;
        }

        // ---- Google Consent Mode v2 ----
        // GCM is the actual gate for gtag() transmission. When the host site
        // uses it, prefer it over TCF: TCF may say "consent granted" while
        // GCM still has analytics_storage denied, and gtag will drop the hit.
        // Hook dataLayer.push to react to user grant/revoke after page load.
        const gcmInitial = readGcmConsent();
        if (gcmInitial) {
            console.log('[Fireknife] Google Consent Mode v2 detected:', gcmInitial);
            setConsent(mapGcmToPlatforms(gcmInitial));
            setCmpChecked(true);

            const dl = window.dataLayer as unknown[] & { push: (...args: unknown[]) => number };
            const origPush = dl.push.bind(dl);
            dl.push = function (...args: unknown[]) {
                const result = origPush(...args);
                for (const a of args) {
                    const arr = Array.isArray(a)
                        ? a
                        : a && typeof (a as { length?: number }).length === 'number'
                          ? Array.from(a as ArrayLike<unknown>)
                          : null;
                    if (arr && arr[0] === 'consent') {
                        const fresh = readGcmConsent();
                        if (fresh) {
                            console.log('[Fireknife] GCM consent updated:', fresh);
                            setConsent(mapGcmToPlatforms(fresh));
                        }
                    }
                }
                return result;
            };

            return () => {
                dl.push = origPush;
            };
        }

        const setupCmpListener = () => {
            if (!window.__tcfapi) {
                // No CMP - assume all consent granted (backward compatible)
                console.log('[Fireknife] No CMP detected, assuming all tracking consent granted');
                setConsent(grantConsentForConfiguredIds(ids));
                setCmpChecked(true);
                return;
            }

            console.log('[Fireknife] CMP detected, checking consent...');

            // Listen for consent events from the CMP
            // This allows us to react to user consent changes in real-time
            window.__tcfapi('addEventListener', 2, (tcData: any, success: boolean) => {
                if (!success || !tcData) return;

                // Event types we care about:
                // - tcloaded: Initial consent data loaded
                // - useractioncomplete: User made a consent choice
                if (
                    tcData.eventStatus === 'tcloaded' ||
                    tcData.eventStatus === 'useractioncomplete'
                ) {
                    console.log('[Fireknife] CMP event:', tcData.eventStatus);
                    updateConsentState(ids);
                    setCmpChecked(true);
                }
            });

            // Perform initial consent check immediately
            updateConsentState(ids);
        };

        // CMP might load asynchronously after our script
        // Poll every 100ms to detect when it becomes available
        const checkCmp = setInterval(() => {
            if (window.__tcfapi || cmpChecked) {
                setupCmpListener();
                clearInterval(checkCmp);
            }
        }, 100);

        // Cleanup after 1.5s if CMP still not found.
        // Most CMPs load within a few hundred ms; waiting longer just drops
        // early events (pageView/viewItem) on sites without a CMP.
        const timeout = setTimeout(() => {
            clearInterval(checkCmp);
            if (!cmpChecked) {
                console.log(
                    '[Fireknife] CMP timeout (1.5s), assuming all tracking consent granted',
                );
                setConsent(grantConsentForConfiguredIds(ids));
                setCmpChecked(true);
            }
        }, 1500);

        return () => {
            clearInterval(checkCmp);
            clearTimeout(timeout);
        };
    }, [ids, marketingEnabled, cmpChecked, updateConsentState]);

    const injectScript = useCallback((src: string, id: string) => {
        if (document.getElementById(id)) return;
        const s = document.createElement('script');
        s.async = true;
        s.src = src;
        s.id = id;
        document.head.appendChild(s);
    }, []);

    const initVendors = useCallback(
        (ids: MarketingIds, consentState: ConsentState) => {
            /* eslint-disable prefer-rest-params, prefer-spread, @typescript-eslint/no-unused-expressions, no-var */
            // ---- GA4 ----
            const ga4Ids = getGa4Ids(ids);
            if (ga4Ids.length > 0 && consentState.ga4) {
                if (!window.gtag) {
                    window.dataLayer = window.dataLayer || [];
                    // Use `arguments` (not rest-spread) so gtag/js processes these
                    // as Arguments objects rather than plain arrays.
                    // eslint-disable-next-line prefer-rest-params
                    window.gtag = function gtag() {
                        window.dataLayer.push(arguments);
                    } as any;
                    window.gtag('js', new Date());
                    injectScript(`https://www.googletagmanager.com/gtag/js?id=${ga4Ids[0]}`, 'ga4');
                }

                for (const ga4Id of ga4Ids) {
                    if (configuredGa4Ids.has(ga4Id)) continue;
                    window.gtag('config', ga4Id);
                    configuredGa4Ids.add(ga4Id);
                }
            }

            // ---- Facebook ----
            if (ids.facebook && !window.fbq && consentState.facebook) {
                !(function (f: any, b, e, v, n?, t?, s?) {
                    if (f.fbq) return;
                    n = f.fbq = function () {
                        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
                    };
                    if (!f._fbq) f._fbq = n;
                    n.push = n;
                    n.loaded = !0;
                    n.version = '2.0';
                    n.queue = [];
                    t = b.createElement(e);
                    t.async = !0;
                    t.src = v;
                    s = b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t, s);
                })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
                window.fbq('init', ids.facebook);
            }

            // ---- TikTok ----
            if (ids.tiktok && !window.ttq && consentState.tiktok) {
                !(function (w: any, d: Document, t: string) {
                    w.TiktokAnalyticsObject = t;
                    const tt = (w[t] = w[t] || []);
                    tt.methods = [
                        'page',
                        'track',
                        'identify',
                        'instances',
                        'debug',
                        'on',
                        'off',
                        'once',
                        'ready',
                        'alias',
                        'group',
                        'enableCookie',
                    ];
                    tt.setAndDefer = function (t: any, e: any) {
                        t[e] = function () {
                            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                        };
                    };
                    for (let i = 0; i < tt.methods.length; i++) tt.setAndDefer(tt, tt.methods[i]);
                    tt.load = function (e: string) {
                        const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
                        tt._i = tt._i || {};
                        tt._i[e] = [];
                        tt._i[e]._u = i;
                        tt._t = tt._t || {};
                        tt._t[e] = +new Date();
                        const o = document.createElement('script');
                        o.type = 'text/javascript';
                        o.async = true;
                        o.src = i + '?sdkid=' + e + '&lib=' + t;
                        const a = document.getElementsByTagName('script')[0];
                        a.parentNode.insertBefore(o, a);
                    };
                })(window, document, 'ttq');
                window.ttq.load(ids.tiktok);
                window.ttq.page();
            }

            // ---- LinkedIn ----
            if (ids.linkedin && !window.lintrk && consentState.linkedin) {
                window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                window._linkedin_data_partner_ids.push(ids.linkedin);
                (function (l: any) {
                    if (!l) {
                        window.lintrk = function (a: any, b: any) {
                            window.lintrk.q.push([a, b]);
                        };
                        window.lintrk.q = [];
                    }
                    const s = document.getElementsByTagName('script')[0];
                    const b = document.createElement('script');
                    b.type = 'text/javascript';
                    b.async = true;
                    b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
                    s.parentNode.insertBefore(b, s);
                })(window.lintrk);
            }

            // ---- Twitter ----
            if (ids.twitter && !window.twq && consentState.twitter) {
                !(function (e, t, n, s?, u?, a?) {
                    e.twq ||
                        ((s = e.twq =
                            function () {
                                s.exe ? s.exe.apply(s, arguments) : s.queue.push(arguments);
                            }),
                        (s.version = '1.1'),
                        (s.queue = []),
                        (u = t.createElement(n)),
                        (u.async = true),
                        (u.src = '//static.ads-twitter.com/uwt.js'),
                        (a = t.getElementsByTagName(n)[0]),
                        a.parentNode.insertBefore(u, a));
                })(window, document, 'script');
                window.twq('init', ids.twitter);
            }

            // ---- Snapchat ----
            if (ids.snapchat && !window.snaptr && consentState.snapchat) {
                !(function (e, t, n) {
                    if (e.snaptr) return;
                    var a: any = (e.snaptr = function () {
                        a.handleRequest
                            ? a.handleRequest.apply(a, arguments)
                            : a.queue.push(arguments);
                    });
                    a.queue = [];
                    const s = 'script';
                    const r = t.createElement(s);
                    r.async = true;
                    r.src = n;
                    const u = t.getElementsByTagName(s)[0];
                    u.parentNode.insertBefore(r, u);
                })(window, document, 'https://sc-static.net/scevent.min.js');
                window.snaptr('init', ids.snapchat);
            }

            // ---- Pinterest ----
            if (ids.pinterest && !window.pintrk && consentState.pinterest) {
                !(function () {
                    if (!window.pintrk) {
                        window.pintrk = function () {
                            window.pintrk.queue.push(Array.prototype.slice.call(arguments));
                        };
                        window.pintrk.queue = [];
                        const n = document.createElement('script');
                        n.async = true;
                        n.src = 'https://s.pinimg.com/ct/core.js';
                        const t = document.getElementsByTagName('script')[0];
                        t.parentNode.insertBefore(n, t);
                    }
                })();
                window.pintrk('load', ids.pinterest);
                window.pintrk('page');
            }
            /* eslint-enable prefer-rest-params, prefer-spread, @typescript-eslint/no-unused-expressions, no-var */
        },
        [injectScript],
    );

    const setMarketingEnabled = useCallback((ids: MarketingIds) => {
        setMarketingEnabledState(true);
        setIds(ids);
        // Don't initialize vendors here - wait for consent check
    }, []);

    /**
     * Initialize vendors when consent state is available
     */
    useEffect(() => {
        if (!marketingEnabled || !cmpChecked) return;

        const hasAnyConsent = Object.values(consent).some((c) => c === true);
        if (hasAnyConsent) {
            console.log('[Fireknife] Initializing tracking vendors with consent');
            initVendors(ids, consent);
        } else {
            console.log('[Fireknife] No tracking consent granted, vendors not initialized');
        }
    }, [consent, cmpChecked, marketingEnabled, ids, initVendors]);

    // ---- Event wrappers ----
    const pageView = useCallback(() => {
        console.log('Page View tracked');
        if (!marketingEnabled) return;

        if (consent.ga4) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'page_view' });
        }
        if (window.fbq && consent.facebook) window.fbq('track', 'PageView');
        if (window.ttq && consent.tiktok) window.ttq.track('PageView');
        if (window.twq && consent.twitter) window.twq('track', 'PageView');
        if (window.snaptr && consent.snapchat) window.snaptr('track', 'PAGE_VIEW');
        if (window.pintrk && consent.pinterest) window.pintrk('page');
        if (window.lintrk && consent.linkedin) window.lintrk('track', { conversion_id: 0 });
    }, [marketingEnabled, consent]);

    const viewItem = useCallback(
        (payload: TrackPayload) => {
            console.log('View Item tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('view_item', payload, getGa4Ids(ids));
            if (window.fbq && consent.facebook) window.fbq('track', 'ViewContent', payload);
            if (window.ttq && consent.tiktok) window.ttq.track('ViewContent', payload);
            if (window.twq && consent.twitter) window.twq('track', 'ViewContent', payload);
            if (window.snaptr && consent.snapchat) window.snaptr('track', 'VIEW_CONTENT', payload);
            if (window.pintrk && consent.pinterest) window.pintrk('track', 'ViewContent', payload);
        },
        [marketingEnabled, consent, ids],
    );

    const addToCart = useCallback(
        (payload: TrackPayload) => {
            console.log('Add to Cart tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('add_to_cart', payload, getGa4Ids(ids));
            if (window.fbq && consent.facebook) window.fbq('track', 'AddToCart', payload);
            if (window.ttq && consent.tiktok) window.ttq.track('AddToCart', payload);
            if (window.twq && consent.twitter) window.twq('track', 'AddToCart', payload);
            if (window.snaptr && consent.snapchat) window.snaptr('track', 'ADD_CART', payload);
            if (window.pintrk && consent.pinterest) window.pintrk('track', 'AddToCart', payload);
        },
        [marketingEnabled, consent, ids],
    );

    const beginCheckout = useCallback(
        (payload: TrackPayload) => {
            console.log('Begin Checkout tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('begin_checkout', payload, getGa4Ids(ids));
            if (window.fbq && consent.facebook) window.fbq('track', 'InitiateCheckout', payload);
            if (window.ttq && consent.tiktok) window.ttq.track('InitiateCheckout', payload);
            if (window.twq && consent.twitter) window.twq('track', 'InitiateCheckout', payload);
            if (window.snaptr && consent.snapchat)
                window.snaptr('track', 'START_CHECKOUT', payload);
            if (window.pintrk && consent.pinterest)
                window.pintrk('track', 'InitiateCheckout', payload);
        },
        [marketingEnabled, consent, ids],
    );

    const purchase = useCallback(
        (payload: TrackPayload) => {
            console.log('Purchase tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('purchase', payload, getGa4Ids(ids));
            if (window.fbq && consent.facebook) window.fbq('track', 'Purchase', payload);
            if (window.ttq && consent.tiktok) window.ttq.track('Purchase', payload);
            if (window.twq && consent.twitter) window.twq('track', 'Purchase', payload);
            if (window.snaptr && consent.snapchat) window.snaptr('track', 'PURCHASE', payload);
            if (window.pintrk && consent.pinterest) window.pintrk('track', 'Purchase', payload);
        },
        [marketingEnabled, consent, ids],
    );

    const addShippingInfo = useCallback(
        (payload: TrackPayload) => {
            console.log('Add Shipping Info tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('add_shipping_info', payload, getGa4Ids(ids));
        },
        [marketingEnabled, consent, ids],
    );

    /**
     * GA4 `add_payment_info` ("started paying"). Fired when a guest settles a
     * partial split-payment leg: the balance isn't fully paid yet, so this is a
     * funnel signal, not a conversion. GA4 only — it carries no `transaction_id`
     * and never reaches a revenue report, so it can't double-count the eventual
     * `purchase`. Meta/TikTok have an AddPaymentInfo too, but firing it once per
     * leg would skew their funnel counts, so we keep this to GA4 like `refund`.
     */
    const addPaymentInfo = useCallback(
        (payload: TrackPayload) => {
            console.log('Add Payment Info tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('add_payment_info', payload, getGa4Ids(ids));
        },
        [marketingEnabled, consent, ids],
    );

    /**
     * GA4 standard `refund` event — reverses a prior `purchase` in GA4 revenue
     * reports. Reuse the original purchase `transaction_id`: for a full refund
     * GA4 subtracts the whole transaction by id; pass `items` for partial
     * refunds.
     *
     * Only GA4 defines a standard refund event. Meta/TikTok/etc. have no
     * equivalent pixel event, and a browser pixel can't see admin-initiated
     * refunds anyway — those are best reconciled server-side (e.g. Meta
     * Conversions API / GA4 Measurement Protocol). So we intentionally fan out
     * to GA4 only here.
     */
    const refund = useCallback(
        (payload: TrackPayload) => {
            console.log('Refund tracked', payload);
            if (!marketingEnabled) return;

            if (consent.ga4) pushGa4Ecommerce('refund', payload, getGa4Ids(ids));
        },
        [marketingEnabled, consent, ids],
    );

    return (
        <MarketingContext.Provider
            value={{
                marketingEnabled,
                setMarketingEnabled,
                pageView,
                viewItem,
                addToCart,
                beginCheckout,
                addShippingInfo,
                purchase,
                addPaymentInfo,
                refund,
            }}
        >
            {children}
        </MarketingContext.Provider>
    );
};

export function useMarketing() {
    return useContext(MarketingContext);
}

// ---- window type extensions ----
declare global {
    interface Window {
        gtag?: (...args: any[]) => void;
        fbq?: (...args: any[]) => void;
        ttq?: any;
        lintrk?: any;
        _linkedin_data_partner_ids?: any[];
        twq?: (...args: any[]) => void;
        snaptr?: (...args: any[]) => void;
        pintrk?: (...args: any[]) => void;
        dataLayer?: any[];
        /**
         * IAB TCF 2.0 Consent Management Platform API
         * @see https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
         */
        __tcfapi?: (
            command: string,
            version: number,
            callback: (tcData: any, success: boolean) => void,
            parameter?: any,
        ) => void;
    }
}
